import React, { useState } from "react";
import classNames from "classnames";
import { Counterparty } from "@common/types/counterparty";
import { CounterpartyDetailChanges } from "@common/helpers/counterpartyDetailChanges";
import { CounterpartyReplacePreview } from "@common/helpers/counterpartyDetailChangeWrites";
import { CreateCounterpartyAddressBody } from "@common/schemas/counterpartyAddress.schema";
import { CreateCounterpartyContactBody } from "@common/schemas/counterpartyContact.schema";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CheckIcon } from "@icons";
import { CounterpartyEditorDrawer } from "@components/settings/Directory/CounterpartyEditor/CounterpartyEditorDrawer";
import styles from "@components/invoices/NativeInvoiceDrawer/SidePanel.module.scss";

function addressLabel(address: string): string {
    const normalized = address.toLowerCase();
    if (
        normalized.includes("remit") ||
        normalized.includes("lockbox") ||
        normalized.includes("p.o. box") ||
        normalized.includes("po box")
    ) {
        return "Remit-to";
    }
    return "Address";
}

function changeRows(changes: CounterpartyDetailChanges): string[] {
    return [
        ...changes.emails.map((value) => `Email: ${value}`),
        ...changes.phoneNumbers.map((value) => `Phone: ${value}`),
        ...changes.contactNames.map((value) => `Contact: ${value}`),
        ...changes.addresses.map((value) => `${addressLabel(value)}: ${value}`),
    ];
}

function DetailChangesCheckButton({
    appearance,
    reviewLabel,
    disabled,
    isHighlighted,
    ...triggerProps
}: React.ComponentPropsWithoutRef<"button"> & {
    appearance: "panel" | "cell";
    reviewLabel: string;
    isHighlighted: boolean;
}) {
    switch (appearance) {
        case "panel":
            return (
                <Button
                    {...triggerProps}
                    type="button"
                    variant="secondary"
                    size="xl"
                    iconOnly
                    aria-label={reviewLabel}
                    disabled={disabled}
                    className={classNames(
                        styles.accentIconButton,
                        isHighlighted && styles.reviewTaskBorderHighlight,
                    )}
                >
                    <CheckIcon />
                </Button>
            );
        case "cell":
            return (
                <Button
                    {...triggerProps}
                    type="button"
                    variant="accent"
                    size="md"
                    iconOnly
                    aria-label={reviewLabel}
                    disabled={disabled}
                >
                    <CheckIcon />
                </Button>
            );
        default: {
            const unreachable: never = appearance;
            return unreachable;
        }
    }
}

type EditorAction = "merge" | "replace";

function editorBookSeeds({
    action,
    mergeSeeds,
    replacePreview,
}: {
    action: EditorAction;
    mergeSeeds: {
        contacts: CreateCounterpartyContactBody[];
        addresses: CreateCounterpartyAddressBody[];
    };
    replacePreview: CounterpartyReplacePreview;
}) {
    switch (action) {
        case "merge":
            return {
                contacts: mergeSeeds.contacts,
                addresses: mergeSeeds.addresses,
                contactReplacesIds: [],
                addressReplacesIds: [],
                removedContactIds: [],
                removedAddressIds: [],
            };
        case "replace":
            return {
                contacts: replacePreview.contacts.map((row) => row.body),
                addresses: replacePreview.addresses.map((row) => row.body),
                contactReplacesIds: replacePreview.contacts.map(
                    (row) => row.replacesId,
                ),
                addressReplacesIds: replacePreview.addresses.map(
                    (row) => row.replacesId,
                ),
                removedContactIds: replacePreview.removedContactIds,
                removedAddressIds: replacePreview.removedAddressIds,
            };
        default: {
            const unreachable: never = action;
            return unreachable;
        }
    }
}

interface Props {
    changes: CounterpartyDetailChanges;
    counterparty: Counterparty | null;
    mergeSeeds: {
        contacts: CreateCounterpartyContactBody[];
        addresses: CreateCounterpartyAddressBody[];
    };
    replacePreview: CounterpartyReplacePreview;
    isHighlighted: boolean;
    loadError: string | null;
    reviewLabel: string;
    newCounterpartyAction: string;
    appearance?: "panel" | "cell";
    isDisabled?: boolean;
    onRetry: () => void;
    onMerged: (counterparty: Counterparty) => void;
    onCreateNew: () => void;
    onSkip: () => void;
}

export const AccrualCounterpartyDetailChanges: React.FC<Props> = ({
    changes,
    counterparty,
    mergeSeeds,
    replacePreview,
    isHighlighted,
    loadError,
    reviewLabel,
    newCounterpartyAction,
    appearance = "panel",
    isDisabled = false,
    onRetry,
    onMerged,
    onCreateNew,
    onSkip,
}) => {
    const [editorAction, setEditorAction] = useState<EditorAction | null>(null);
    const bookSeeds = editorBookSeeds({
        action: editorAction ?? "merge",
        mergeSeeds,
        replacePreview,
    });
    const [localizedCounterparty, setLocalizedCounterparty] =
        useState<Counterparty | null>(null);
    const rows = changeRows(changes);

    if (!loadError && rows.length === 0) {
        return null;
    }

    return (
        <>
            <Menu.Root
                align="end"
                trigger={
                    <Menu.Trigger
                        nativeButton
                        render={(triggerProps) => (
                            <Tooltip
                                title={reviewLabel}
                                content={loadError ?? undefined}
                                bullets={loadError ? undefined : rows}
                            >
                                <DetailChangesCheckButton
                                    {...triggerProps}
                                    appearance={appearance}
                                    reviewLabel={reviewLabel}
                                    disabled={isDisabled}
                                    isHighlighted={isHighlighted}
                                />
                            </Tooltip>
                        )}
                    />
                }
            >
                {loadError ? (
                    <Menu.Item onClick={onRetry}>Retry</Menu.Item>
                ) : (
                    <>
                        <Menu.Item
                            disabled={!counterparty}
                            onClick={() => setEditorAction("merge")}
                        >
                            Merge
                        </Menu.Item>
                        <Menu.Item
                            disabled={!counterparty}
                            onClick={() => setEditorAction("replace")}
                        >
                            Replace
                        </Menu.Item>
                        <Menu.Item onClick={onCreateNew}>
                            {newCounterpartyAction}
                        </Menu.Item>
                    </>
                )}
                <Menu.Item onClick={onSkip}>Skip</Menu.Item>
            </Menu.Root>
            {counterparty ? (
                <CounterpartyEditorDrawer
                    open={editorAction !== null}
                    isCreating={false}
                    counterparty={localizedCounterparty ?? counterparty}
                    onClose={() => {
                        setEditorAction(null);
                        if (localizedCounterparty) {
                            onMerged(localizedCounterparty);
                        }
                    }}
                    onCreated={() => undefined}
                    onEdited={(edited) => {
                        setEditorAction(null);
                        onMerged(edited);
                    }}
                    onLocalized={setLocalizedCounterparty}
                    seedContacts={bookSeeds.contacts}
                    seedAddresses={bookSeeds.addresses}
                    seedContactReplacesIds={bookSeeds.contactReplacesIds}
                    seedAddressReplacesIds={bookSeeds.addressReplacesIds}
                    removedContactIds={bookSeeds.removedContactIds}
                    removedAddressIds={bookSeeds.removedAddressIds}
                />
            ) : null}
        </>
    );
};
